import { useEffect, useRef } from "react";

/** True when the element is already visible in the upper part of the viewport. */
export function isAlertVisibleNearTop(el) {
  if (!el || typeof el.getBoundingClientRect !== "function") return true;
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  return rect.top >= 0 && rect.top < viewportHeight * 0.35;
}

/** Smoothly scroll so an alert/message is visible; no-op if already on screen. */
export function scrollElementIntoView(el, { behavior = "smooth" } = {}) {
  if (!el || isAlertVisibleNearTop(el)) return;
  requestAnimationFrame(() => {
    el.scrollIntoView({ behavior, block: "start" });
    if (!el.hasAttribute("tabindex")) el.tabIndex = -1;
    el.focus({ preventScroll: true });
  });
}

/**
 * Scroll a ref into view when `active` becomes truthy or its signature changes.
 * Use for error/warning cards shown after user actions at the bottom of long pages.
 */
export default function useAutoScrollIntoView(active, { enabled = true } = {}) {
  const ref = useRef(null);
  const signatureRef = useRef(null);

  useEffect(() => {
    if (!enabled || !active) return;
    const el = ref.current;
    if (!el) return;

    const signature = typeof active === "string" ? active : "active";
    if (signatureRef.current === signature) return;
    signatureRef.current = signature;

    scrollElementIntoView(el);
  }, [enabled, active]);

  return ref;
}
